import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n<div\n  class=\"cST--container--top ember-view {{if @selectedStory 'has--selected'}}\"\n  {{did-update\n    this.updateVisTimeline\n    @socialProfile.profileColorClass\n    this.startMoment\n    this.endMoment\n    this.storiesHaveUpdated\n  }}\n  {{droppable onDrop=this.handleDrop}}\n>\n  <div class=\"cST--header\">\n    <span class=\"cST--header__title\">\n      {{t \"stories.timeline\"}}\n    </span>\n  </div>\n  <div class=\"cST--content eST--timeline gd--story--2\" {{did-insert this.drawTimeline}} {{perfect-scroll}}></div>\n  {{#unless @showStoryEditor}}\n    <div class=\"cST--timeline__hint\" {{on \"click\" @goToCalendar}}>\n      {{t \"stories.exit_to_edit_posts\"}}\n    </div>\n  {{/unless}}\n  {{#if @showNewStoryDropZone}}\n    <div class=\"cDZ--dropzoneWrapper--stories\">\n      <div class=\"cDZ--dropzone\">\n        <span class=\"cDZ--dropzone__text\">\n          {{t \"stories.timeline_dropzone_message\"}}\n        </span>\n      </div>\n    </div>\n  {{/if}}\n</div>", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n<div\n  class=\"cST--container--top ember-view {{if @selectedStory 'has--selected'}}\"\n  {{did-update\n    this.updateVisTimeline\n    @socialProfile.profileColorClass\n    this.startMoment\n    this.endMoment\n    this.storiesHaveUpdated\n  }}\n  {{droppable onDrop=this.handleDrop}}\n>\n  <div class=\"cST--header\">\n    <span class=\"cST--header__title\">\n      {{t \"stories.timeline\"}}\n    </span>\n  </div>\n  <div class=\"cST--content eST--timeline gd--story--2\" {{did-insert this.drawTimeline}} {{perfect-scroll}}></div>\n  {{#unless @showStoryEditor}}\n    <div class=\"cST--timeline__hint\" {{on \"click\" @goToCalendar}}>\n      {{t \"stories.exit_to_edit_posts\"}}\n    </div>\n  {{/unless}}\n  {{#if @showNewStoryDropZone}}\n    <div class=\"cDZ--dropzoneWrapper--stories\">\n      <div class=\"cDZ--dropzone\">\n        <span class=\"cDZ--dropzone__text\">\n          {{t \"stories.timeline_dropzone_message\"}}\n        </span>\n      </div>\n    </div>\n  {{/if}}\n</div>","moduleName":"later/components/schedule/stories/vis-timeline.hbs","parseOptions":{"srcName":"later/components/schedule/stories/vis-timeline.hbs"}});
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { isEqual } from 'lodash';
import moment from 'moment';
import { Timeline, DataSet } from 'vis-timeline/standalone';

import { profileColorOutlineClass } from 'later/helpers/profile-color-outline-class';
import { convert } from 'later/utils/time-format';

import type IntlService from 'ember-intl/services/intl';
import type GramModel from 'later/models/gram';
import type SocialProfileModel from 'later/models/social-profile';
import type ConfigService from 'later/services/calendar/config';
import type DialogManagerService from 'later/services/dialog-manager';
import type { Moment } from 'moment';
import type { TimelineItem, DateType } from 'vis-timeline/standalone';
interface DateRange {
  start: DateType;
  end: DateType;
}

interface VisLocales {
  intlTranslations: {
    current: string;
    time: string;
    deleteSelected: string;
  };
}

interface VisTimelineComponentArgs {
  openStory: (id: string) => void;
  discardChangesAndClose: () => void;
  selectedStory: GramModel;
  currentTimeZoneIdentifier: string;
  stories: GramModel[];
  socialProfile: SocialProfileModel;
  updateStoryTime: (id: string, time: number) => void;
  dropNewStoryOnTimeline: (dragEvent: DragEvent, time: number) => void;
}

export default class VisTimelineComponent extends Component<VisTimelineComponentArgs> {
  @service('calendar/config') declare calendarConfig: ConfigService;
  @service declare dialogManager: DialogManagerService;
  @service declare intl: IntlService;

  visTimeline?: Timeline;
  storyItems?: TimelineItem[];

  get locales(): VisLocales {
    return {
      intlTranslations: {
        current: this.intl.t('calendar.vis_timeline.current'),
        time: this.intl.t('calendar.vis_timeline.time'),
        deleteSelected: this.intl.t('calendar.vis_timeline.deleteSelected')
      }
    };
  }

  get startMoment(): Moment {
    const { startTime } = this.calendarConfig;
    const startMoment = startTime ? moment(startTime) : moment();
    return startMoment.add(1, 'second');
  }

  get endMoment(): Moment {
    const { endTime } = this.calendarConfig;
    const endMoment = endTime ? moment(endTime) : moment();
    return endMoment.subtract(1, 'second');
  }

  get storiesHaveUpdated(): boolean {
    return isEqual(this.#buildStoryDataObjects(), this.storyItems);
  }

  @action
  drawTimeline(container: HTMLElement): void {
    const timeZone = this.args.currentTimeZoneIdentifier;
    const utcOffset = moment.tz(timeZone).utcOffset();

    // Note: Configuration for the Timeline
    const options = {
      ...this.#generateDateRange(),
      horizontalScroll: false,
      zoomable: false,
      moveable: false,
      minHeight: 230,
      format: {
        minorLabels: {
          hour: 'ha'
        }
      },
      timeAxis: {
        scale: 'hour',
        step: 6
      },
      itemsAlwaysDraggable: true,
      orientation: {
        axis: 'top',
        item: 'top'
      },
      moment(date: DateType): Moment {
        return moment(date).utcOffset(utcOffset);
      },
      editable: {
        add: false, // add new items by double tapping
        updateTime: true, // drag items horizontally
        updateGroup: false, // drag items from one group to another
        remove: false, // delete an item by tapping the delete button top right
        overrideItems: false // allow these options to override item.editable
      },
      // Note: Instead of using built in locale support, use translations provided by ember-intl
      locales: this.locales,
      locale: 'intlTranslations',
      onMove: (item: TimelineItem /*, callback */) => {
        this.#handleStoryMove(item.id as string, moment(item.start));
      },
      snap(date: number /*, scale, step*/) {
        const hour = convert.hour().toMilliseconds();
        return Math.round(date / hour) * hour;
      }
    };

    const storyItems = this.#buildStoryDataObjects();
    this.storyItems = storyItems;

    const visTimeline = new Timeline(container, storyItems, options);
    this.visTimeline = visTimeline;

    visTimeline.on('select', (properties) => {
      const selectedItem = properties.items[0];
      if (selectedItem) {
        this.args.openStory(selectedItem);
      }
    });

    visTimeline.on('click', (properties) => {
      if (isEmpty(properties.item)) {
        this.args.discardChangesAndClose();
      }
    });
  }

  @action
  handleDrop(dragEvent: DragEvent): void {
    const timeDroppedTo = this.#calculateTimeDroppedTo(dragEvent);
    this.args.dropNewStoryOnTimeline(dragEvent, timeDroppedTo);
  }

  @action
  updateVisTimeline(): void {
    const storyItems = this.#buildStoryDataObjects();
    this.visTimeline?.setOptions(this.#generateDateRange());
    this.#updateTimelineStories();
    this.visTimeline?.setItems(storyItems);
    this.storyItems = storyItems;
  }

  #buildStoryDataObjects(): TimelineItem[] {
    const stories = this.args.stories.map((story: GramModel) => {
      const storyContent = this.#generateStoryContent(story);
      const storyClasses = this.#generateStoryClasses(story);
      const canDragOnTimeline = isEmpty(this.args.selectedStory);
      const canDragStory = !story.isPosted && !story.isNew && !story.isSaving && canDragOnTimeline;

      return {
        id: story.id,
        className: storyClasses,
        content: storyContent,
        start: story.startTime || moment(),
        end: story.storyEndTime || moment(),
        editable: { updateTime: canDragStory, updateGroup: false, remove: false }
      };
    });

    return new DataSet(stories);
  }

  #calculateTimeDroppedTo(dragEvent: DragEvent): number {
    const timelineContainer = document.querySelector('.vis-center');
    const containerProperties = timelineContainer?.getBoundingClientRect() || { left: 0, width: 0 };
    const { left, width } = containerProperties;
    const offsetEventX = dragEvent.pageX - 27; // hack to align drop time to left edge of dropped picture --GP
    const xValue = offsetEventX < left ? left : offsetEventX;
    const droppedTimelineDay = (xValue - left) / (width / 7);
    const startDayUnix = this.startMoment.unix();
    return Math.floor(startDayUnix + droppedTimelineDay * convert.day().toSeconds());
  }

  #generateDateRange(): DateRange {
    return {
      start: this.startMoment.toDate(),
      end: this.endMoment.toDate()
    };
  }

  #generateStoryClasses(story: GramModel): string {
    const storyClasses = [];
    const storyItemBaseClass = this.args.selectedStory?.id == story.id ? 'is--selected' : 'is--unselected';

    storyClasses.push(storyItemBaseClass);
    storyClasses.push(profileColorOutlineClass([this.args.socialProfile.profileColorClass]));

    if (story.isPosted) {
      storyClasses.push('is--posted');
    }

    // Note: Reason for '.get' https://github.com/typed-ember/ember-cli-typescript/issues/969
    if (story.get('isSaving')) {
      storyClasses.push('is--loading');
    }

    return storyClasses.join(' ');
  }

  #generateStoryContent(story: GramModel): HTMLDivElement {
    const storyThumbnails = story.postMediaItems.map(
      (mediaItem) => `<span class='vis-item_img' style='background-image: url(${mediaItem.smallThumbnailUrl})'></span>`
    );

    const timeElement = document.createElement('span');
    timeElement.classList.add('vis-time');

    timeElement.innerHTML = moment(story.startTime || '')
      .tz(this.args.currentTimeZoneIdentifier)
      .format('h:mm A');

    const thumbnailListElement = document.createElement('div');
    thumbnailListElement.classList.add('vis-list');
    thumbnailListElement.innerHTML = storyThumbnails.join('');
    const content = document.createElement('div');
    content.appendChild(timeElement);
    content.appendChild(thumbnailListElement);

    this.#addStoriesPublishingIcons(story, content);

    return content;
  }

  #addStoriesPublishingIcons(story: GramModel, content: HTMLDivElement): void {
    const publishMethodContainer = document.createElement('span');
    publishMethodContainer.classList.add('vis-method');
    const publishMethodIcon = document.createElement('span');
    publishMethodIcon.classList.add(story.isNotificationPost ? 'fc-icon-notify--white' : 'fc-icon-instant--white');
    publishMethodContainer.innerHTML = story.isNotificationPost
      ? this.intl.t('calendar.publish_methods.notification')
      : this.intl.t('calendar.publish_methods.auto_publish');
    publishMethodContainer.appendChild(publishMethodIcon);
    content.appendChild(publishMethodContainer);
  }

  #updateTimelineStories(): void {
    const storyItems = this.#buildStoryDataObjects();
    this.visTimeline?.setItems(storyItems);
    this.storyItems = storyItems;
  }

  #handleStoryMove(storyId: string, itemNewStart: Moment): void {
    const now = moment().unix();
    const newStartTime = moment(itemNewStart).unix();

    if (newStartTime <= now) {
      this.dialogManager
        .confirm(this.intl.t('calendar.cannot_schedule_in_past'), {
          description: this.intl.t('calendar.cannot_post_path_immediate'),
          confirmButton: this.intl.t('shared_phrases.publish_now')
        })
        .then(() => this.args.updateStoryTime(storyId, moment().unix()))
        .catch(() => this.#updateTimelineStories());
    } else {
      this.args.updateStoryTime(storyId, newStartTime);
    }
  }
}
